/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useState, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { TableEntry } from '~src/utils/securityGroupsConfig';

interface ReviewFolderTreeProps {
  folders: TableEntry[];
  selectedFolderIds: Set<number>;
  onRemove: (folderId: number) => void;
}

export const ReviewFolderTree: React.FC<ReviewFolderTreeProps> = ({
  folders,
  selectedFolderIds,
  onRemove,
}) => {
  const styles = useStyles(styleFn);
  const [expandedFolderIds, setExpandedFolderIds] = useState<Set<number>>(
    new Set()
  );

  // Get all folder IDs recursively
  const getAllFolderIds = (folderList: TableEntry[]): number[] => {
    const ids: number[] = [];
    const collectIds = (folders: TableEntry[]) => {
      folders.forEach((folder) => {
        ids.push(folder.dbFolder.id);
        if (folder.children && folder.children.length > 0) {
          collectIds(folder.children);
        }
      });
    };
    collectIds(folderList);
    return ids;
  };

  // Initialize with all folders expanded
  useEffect(() => {
    if (folders.length > 0 && expandedFolderIds.size === 0) {
      const allIds = getAllFolderIds(folders);
      setExpandedFolderIds(new Set(allIds));
    }
  }, [folders]);

  // Handle toggle expand
  const handleToggleExpand = (folderId: number) => {
    setExpandedFolderIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };

  return (
    <div css={styles.tree}>
      {folders.map((folder, index) => (
        <ReviewFolderItem
          key={folder.key}
          folder={folder}
          level={0}
          selectedFolderIds={selectedFolderIds}
          onRemove={onRemove}
          expandedFolderIds={expandedFolderIds}
          onToggleExpand={handleToggleExpand}
          parentChain={[]}
          isLastChild={index === folders.length - 1}
        />
      ))}
    </div>
  );
};

interface ReviewFolderItemProps {
  folder: TableEntry;
  level: number;
  selectedFolderIds: Set<number>;
  onRemove: (folderId: number) => void;
  expandedFolderIds: Set<number>;
  onToggleExpand: (folderId: number) => void;
  parentChain: boolean[];
  isLastChild: boolean;
}

const ReviewFolderItem: React.FC<ReviewFolderItemProps> = ({
  folder,
  level,
  selectedFolderIds,
  onRemove,
  expandedFolderIds,
  onToggleExpand,
  parentChain,
  isLastChild,
}) => {
  const styles = useStyles(styleFn);
  const intl = useIntl();

  const hasChildren = folder.children && folder.children.length > 0;
  const isExpanded = expandedFolderIds.has(folder.dbFolder.id);

  return (
    <div css={styles.itemWrapper}>
      <div css={[styles.item, { paddingLeft: level * 20 + 12 }]}>
        {/* 1. Tree lines - only for nested folders */}
        {parentChain.length > 0 && (
          <div css={styles.treeLines}>
            {/* Show empty spacers for ancestors to maintain indentation */}
            {parentChain.slice(0, -1).map((_, idx) => (
              <div key={idx} css={styles.treeLine} />
            ))}
            {/* Show connection line only for immediate parent */}
            <div
              css={[
                styles.treeLine,
                isLastChild ? styles.treeLineL : styles.treeLineT,
              ]}
            />
          </div>
        )}

        {/* 2. Chevron container - always present for alignment */}
        <div css={styles.chevronContainer}>
          {hasChildren && (
            <FontAwesomeIcon
              icon={['fas', isExpanded ? 'chevron-down' : 'chevron-right']}
              css={styles.chevronIcon}
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand(folder.dbFolder.id);
              }}
            />
          )}
        </div>

        {/* 3. Folder icon - with orange color like Step 2 */}
        <FontAwesomeIcon icon={['fas', 'folder']} css={styles.folderIcon} />

        {/* 4. Folder name */}
        <span css={styles.folderName}>{folder.title}</span>

        {/* 5. Description */}
        {folder.dbFolder.description && (
          <span css={styles.folderDescription}>
            - {folder.dbFolder.description}
          </span>
        )}

        {/* 6. Remove button - keep existing */}
        <button
          onClick={() => onRemove(folder.dbFolder.id)}
          css={styles.removeButton}
          title={intl.formatMessage({ id: 'remove-folder' })}
          type="button">
          <FontAwesomeIcon icon={['far', 'times']} />
        </button>
      </div>

      {hasChildren && isExpanded && (
        <div css={styles.children}>
          {folder.children!.map((child, index) => (
            <ReviewFolderItem
              key={child.key}
              folder={child}
              level={level + 1}
              selectedFolderIds={selectedFolderIds}
              onRemove={onRemove}
              expandedFolderIds={expandedFolderIds}
              onToggleExpand={onToggleExpand}
              parentChain={[...parentChain, isLastChild]}
              isLastChild={index === folder.children!.length - 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  tree: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    maxHeight: 400,
    overflowY: 'auto',
    paddingRight: 8,
  },
  itemWrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 12px',
    backgroundColor: theme.colors.backgroundSecondary,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: 6,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: theme.colors.primaryLight,
      borderColor: theme.colors.primary,
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    },
  },
  chevronContainer: {
    width: 20,
    height: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  chevronIcon: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    cursor: 'pointer',
    transition: 'color 0.2s ease',
    flexShrink: 0,
    '&:hover': {
      color: theme.colors.primary,
    },
  },
  treeLines: {
    display: 'flex',
    alignItems: 'stretch',
    alignSelf: 'stretch',
    flexShrink: 0,
  },
  treeLine: {
    width: 24,
    position: 'relative',
    flexShrink: 0,
  },
  treeLineT: {
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 11,
      top: '-4px',
      bottom: '-4px',
      width: '2px !important',
      backgroundColor: '#8c8c8c !important',
      opacity: 0.6,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 13,
      top: '50%',
      width: '12px !important',
      height: '2px !important',
      backgroundColor: '#8c8c8c !important',
      opacity: 0.6,
    },
  },
  treeLineL: {
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 13,
      top: '50%',
      width: '12px !important',
      height: '2px !important',
      backgroundColor: '#8c8c8c !important',
      opacity: 0.6,
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 11,
      top: '-4px',
      bottom: '50%',
      width: '2px !important',
      backgroundColor: '#8c8c8c !important',
      opacity: 0.6,
    },
  },
  folderIcon: {
    fontSize: 16,
    color: '#faad14 !important',
    flexShrink: 0,
  },
  folderName: {
    fontSize: 14,
    fontWeight: 500,
    color: theme.colors.text.primary,
  },
  folderDescription: {
    fontSize: 13,
    color: theme.colors.textSecondary,
    fontStyle: 'italic',
    marginLeft: 4,
  },
  removeButton: {
    padding: '4px 8px',
    height: '24px',
    minWidth: '24px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    flexShrink: 0,
    marginLeft: 'auto',
    backgroundColor: '#ffffff',
    border: '1px solid #d9d9d9',
    borderRadius: '4px',
    color: '#595959',
    cursor: 'pointer',
    transition: 'all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1)',
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: 1,
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
    '&:hover': {
      backgroundColor: '#ffffff',
      borderColor: '#ff4d4f',
      color: '#ff4d4f',
      boxShadow: '0 2px 4px rgba(255, 77, 79, 0.15)',
    },
    '&:active': {
      backgroundColor: '#f5f5f5',
      borderColor: '#d9363e',
      color: '#d9363e',
      transform: 'translateY(1px)',
    },
    '&:focus': {
      outline: 'none',
      borderColor: '#ff7875',
      boxShadow: '0 0 0 2px rgba(255, 77, 79, 0.1)',
    },
    '& svg': {
      fontSize: '12px',
      color: 'inherit',
    },
  },
  children: {
    marginTop: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
});
